import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, runInAction } from 'mobx';

import RendererSyncService, {
  resolveResetProps,
  resolveResetPropsMap,
} from './services/RendererSyncService';
import { EditorChangeType } from './types';
import { VideoEditor } from './VideoEditor';

import type { IVideoEditor } from './interface/IVideoEditor';
import type { Timeline } from './models/Timeline';
import type { RendererState } from '@src/renderer/RendererState';
import type { VideoSource } from '@src/stores-v2';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

const logger = LogManager.getLogger('QuoteCardsEditorStore');

/**
 * QuoteCardsEditorStore is a simplified MobX wrapper around VideoEditor
 * for quote card rendering. It only depends on VideoSourceStore.
 */
export class QuoteCardsEditorStore {
  // Core state
  editor: IVideoEditor;
  timeline: Timeline;
  isReady: boolean;

  // Headless integration control
  private isLocalChange = false;

  // Selection state
  selectedElementIds: string[] = [];

  // External sync service for iframe interactions
  private rendererExternalSyncService: RendererSyncService | null = null;
  private externalChangeCount = 0;

  private videoSourceStore: VideoSourceStore;

  constructor(videoSourceStore: VideoSourceStore) {
    this.videoSourceStore = videoSourceStore;
    this.editor = new VideoEditor();
    this.timeline = this.editor.timeline;

    this.isReady = runInAction(() => false);

    this.initializeEditor();
    this.initializeRendererSyncService();

    makeAutoObservable(this, {
      editor: false,
    } as any);
  }

  /**
   * Initializes the editor with observers
   */
  initializeEditor(): void {
    // Register MobX reactivity observer
    this.setupEditorObservers();
  }

  /**
   * Initialize renderer sync service for accepting external changes from iframe
   */
  private initializeRendererSyncService(): void {
    // Simplified sync service for quote cards (no scenes, no reframing)
    this.rendererExternalSyncService = new RendererSyncService({
      layoutIndexStore: {
        getRepresentationRoleRecordFor: () => undefined, // No scene layouts in quote cards
      },
      getScene: () => undefined, // No scenes in quote cards
      updateScene: () => false, // No scenes in quote cards
      updateElement: (elementId, updates) => {
        try {
          return this.acceptExternalChanges(() => {
            return this.editor.commandUpdateElement(
              elementId,
              resolveResetProps(this.editor.getElementById(elementId), updates),
            );
          });
        } catch (e) {
          logger.error('Error updating element in QuoteCard RendererSync', e);
          return false;
        }
      },
      updateElements: updates => {
        try {
          this.acceptExternalChanges(() => {
            this.editor.commandUpdateMultipleElements(
              resolveResetPropsMap(updates, id =>
                this.editor.getElementById(id),
              ),
            );
          });
        } catch (e) {
          logger.error(
            'Error updating multiple elements in QuoteCard RendererSync',
            e,
          );
        }
      },
    });
  }

  /**
   * Setup observers to ensure MobX reactivity for editor changes
   */
  private setupEditorObservers(): void {
    const mobxReactivityObserver = {
      onEditorChange: (editor: VideoEditor, changeType: EditorChangeType) => {
        logger.info('OnEditorChange', changeType);
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_REMOVED ||
          changeType === EditorChangeType.ELEMENT_UPDATED ||
          changeType === EditorChangeType.PROJECT_LOADED ||
          changeType === EditorChangeType.COMMAND_UNDONE ||
          changeType === EditorChangeType.COMMAND_REDONE
        ) {
          if (this.isLocalChange) return;
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
          } finally {
            this.isLocalChange = false;
          }
        }

        // Handle forced refresh after external changes batch complete
        if (changeType === EditorChangeType.FORCED_REFRESH) {
          if (this.isLocalChange || this.externalChangeCount === 0) return;
          logger.info(
            `FORCED_REFRESH processing ${this.externalChangeCount} external changes`,
          );
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
            this.externalChangeCount = 0;
          } finally {
            this.isLocalChange = false;
          }
          return;
        }
      },
    };

    this.editor.registerObserver(mobxReactivityObserver);
  }

  /**
   * Force refresh the video source to trigger MobX reactivity.
   *
   * Quote cards always use `toRenderJSON` (full keyframe merge with
   * rendering-grade timing adjustments) because they are published as-is
   * and don't need preview-specific layout strategies or split windows.
   */
  refreshTimeline(): void {
    const json = this.editor.timeline.toRenderJSON();

    const { tempEnhancements, ...newSource } = json;
    // Preserve fonts from the current source
    const currentFonts = this.videoSourceStore.source?.fonts;
    const sourceWithFonts = currentFonts
      ? { ...newSource, fonts: currentFonts }
      : newSource;

    runInAction(() => {
      this.videoSourceStore.setSource(sourceWithFonts);
    });
  }

  /**
   * Accepts external changes from iframe without triggering timeline refresh
   * This prevents circular updates between editor and renderer
   */
  private acceptExternalChanges<T>(action: () => T): T {
    try {
      this.isLocalChange = true;
      this.externalChangeCount++;
      return action();
    } finally {
      this.isLocalChange = false;
    }
  }

  /**
   * Sync external changes from iframe to editor state
   * Only call this for user-driven changes from the iframe preview (drag, resize, etc.)
   */
  syncExternalChanges(
    prevState: RendererState | undefined,
    newState: RendererState,
  ): void {
    if (!this.rendererExternalSyncService) {
      logger.warn('External sync service not initialized');
      return;
    }
    this.rendererExternalSyncService.sync(prevState, newState);
  }

  /**
   * Load a video source into the editor
   */
  loadVideoSource(videoSource: VideoSource): void {
    this.editor.loadVideoSource(videoSource);
  }

  /**
   * Select an element in the timeline (no-op for quote cards)
   * This method is required by EditorStoreLike interface
   */
  selectElement(id: string): void {
    this.selectedElementIds = [id];
    // This is a no-op implementation to satisfy the interface
  }
}
